import React, { useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faPhone,
  faUsers,
  faTag,
  faBars,
  faUser,
  faEdit,
  faDeleteLeft,
} from '@fortawesome/free-solid-svg-icons';
import useViewContact from './useViewContact';
import MainLayout from '../../_layout/MainLayout';
import { Spin } from 'antd';
import { platformRoles } from '../../_helpers/types';
import { AuthContext } from '../../_hooks/useAuthContext';
import ContactDeleteModal from '../../_component/ContactDeleteModal';
import ContactModal from '../../_component/ContactModal';
import MicrosoftLogin from '../../_component/MicrosoftLogin';
import { Tabs } from 'antd';
import { dateFormatter } from '../../_helpers/dateFormatter';
const { TabPane } = Tabs;

function ViewContact() {
  const { user } = useContext(AuthContext);
  const {
    deleteModalOpen,
    contactModalOpen,
    is_microsoft_connected,
    contactDetails,
    loading,
    microsoftEmailThreadsLoading,
    handleContactModalOpen,
    handleDeleteModalOpen,
    tabChangeHandler,
    emailThreads,
  } = useViewContact();

  return (
    <MainLayout activeMenuItem="Contacts">
      <div className="p-4 sticky">
        <div className="centered justify-between mb-4 window-container">
          <h2 className="window-title">View Contact Details</h2>
          <div className="centered justify-center">
            <button
              className="m-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              onClick={() => handleContactModalOpen()}
            >
              <FontAwesomeIcon icon={faEdit} /> Edit
            </button>
            <button
              className="m-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              onClick={() => handleDeleteModalOpen()}
            >
              <FontAwesomeIcon icon={faDeleteLeft} /> Delete
            </button>
          </div>
        </div>
        <hr className="my-4" />
        {loading ? (
          <div className="centered spinner-box">
            <Spin size="large" />
          </div>
        ) : (
          <div className="flex flex-col md:flex-row gap-6 ml-4">
            <div className="w-full md:w-5/12">
              {/* Profile header */}
              <div className="flex items-center mb-5">
                <div className="flex items-center">
                  <div>
                    <h2 className="text-2xl font-bold m-0">{contactDetails?.name}</h2>
                    {Array.isArray(contactDetails?.category_ids) && contactDetails.category_ids.length > 0 && (
                      <button className="mt-2 px-3 py-1 border border-gray-300 rounded-md flex items-center gap-1 text-sm">
                        <FontAwesomeIcon icon={faTag} />{' '}
                        {contactDetails.category_ids.map((category) => category?.title).join(', ')}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <hr className="my-4" />

              {/* About this contact section */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium m-0">About this contact</h3>
                </div>

                <div className="flex justify-between items-center mb-4">
                  <span className="text-gray-500">{contactDetails?.notes}</span>
                </div>
                <h3 className="text-lg font-medium my-4">Details</h3>

                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-500">
                      <FontAwesomeIcon icon={faBars} /> Job Title
                    </span>
                    <span>{contactDetails?.job_title}</span>
                  </div>

                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-500">
                      <FontAwesomeIcon icon={faEnvelope} /> Personal email
                    </span>
                    <div className="flex justify-between items-center">
                      <a href={`mailto:${contactDetails?.email}`} className="text-blue-500">
                        {contactDetails?.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-500">
                      <FontAwesomeIcon icon={faPhone} /> Phone Number
                    </span>
                    <div className="flex justify-between items-center">
                      <a href={`tel:${contactDetails?.contact_number}`} className="text-blue-500">
                        {contactDetails?.contact_number}
                      </a>
                    </div>
                  </div>

                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-500">
                      <FontAwesomeIcon icon={faUsers} /> Company Name
                    </span>
                    <span>{contactDetails?.company_id?.name}</span>
                  </div>

                  {user?.role === platformRoles.admin && (
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-500">
                        <FontAwesomeIcon icon={faUser} /> Added by
                      </span>
                      <span>{contactDetails?.user_id?.full_name}</span>
                    </div>
                  )}
                </div>

                <hr className="my-4" />
              </div>
            </div>

            {/* Right column */}
            <div className="w-full md:w-7/12">
              <div>
                <h3 className="text-lg font-bold mb-4">Email Threads</h3>

                {microsoftEmailThreadsLoading ? (
                  <div className="centered spinner-box">
                    <Spin size="large" />
                  </div>
                ) : (
                  <div>
                    {is_microsoft_connected?.is_connected ? (
                      <div>
                        <Tabs defaultActiveKey="1" onChange={(key) => tabChangeHandler(key)}>
                          <TabPane tab="Received" key="inbox">
                            {(emailThreads || []).map((thread) => (
                              <div
                                key={thread?.received_date}
                                className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm"
                              >
                                <div className="flex justify-between items-center mb-2">
                                  <p className="text-gray-800 font-semibold text-lg">{thread?.subject}</p>
                                  <span className="text-gray-500 text-sm">
                                    {thread?.received_date && dateFormatter(thread?.received_date)}
                                  </span>
                                </div>
                                <div className="flex items-center mb-2">
                                  <div className="w-8 h-8 flex items-center justify-center bg-orange-500 rounded-full text-white mr-3">
                                    @
                                  </div>
                                  <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded">
                                    {contactDetails?.email}
                                  </span>
                                </div>
                                <div className="mt-2 mb-4">
                                  <div dangerouslySetInnerHTML={{ __html: thread?.body_content }} />
                                </div>
                              </div>
                            ))}
                          </TabPane>
                          <TabPane tab="Sent" key="sent">
                            {(emailThreads || []).map((thread) => (
                              <div
                                key={thread?.sent_date}
                                className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm"
                              >
                                <div className="flex justify-between items-center mb-2">
                                  <p className="text-gray-800 font-semibold text-lg">{thread?.subject}</p>
                                  <span className="text-gray-500 text-sm">
                                    {thread?.sent_date && dateFormatter(thread?.sent_date)}
                                  </span>
                                </div>
                                <div className="flex items-center mb-2">
                                  <div className="w-8 h-8 flex items-center justify-center bg-orange-500 rounded-full text-white mr-3">
                                    @
                                  </div>
                                  <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded">
                                    {contactDetails?.email}
                                  </span>
                                </div>
                                <div className="mt-2 mb-4">
                                  <div dangerouslySetInnerHTML={{ __html: thread?.body_content }} />
                                </div>
                              </div>
                            ))}
                          </TabPane>
                        </Tabs>
                      </div>
                    ) : (
                      <div>
                        <p className="mb-4 text-sm text-red-500">
                          You have connected your Microsoft account to this contact.
                        </p>
                        <MicrosoftLogin title="Connect Microsoft" />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <ContactDeleteModal
          deleteContact={contactDetails}
          deleteModalOpen={deleteModalOpen}
          handleDeleteModalOpen={handleDeleteModalOpen}
        />
        <ContactModal
          contactModalOpen={contactModalOpen}
          handleContactModalOpen={handleContactModalOpen}
          contactId={contactDetails?._id}
        />
      </div>
    </MainLayout>
  );
}

export default ViewContact;
